import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const TEAL_BORDER = "rgba(79,205,196,0.35)";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const TABLE_BORDER = "rgba(255,255,255,0.08)";
const AMBER = "#E0A458";

const POST_URL = "https://www.ai-advisors.ai/blog/do-b2b-saas-companies-publish-llms-txt";
const POST_DATE = "2026-10-01";
const CSV_URL = "/data/llms-txt-adoption-2026-10-01.csv";
const JSON_URL = "/data/llms-txt-adoption-2026-10-01.json";
const GOOGLE_GUIDE = "https://developers.google.com/search/docs/fundamentals/ai-optimization-guide";
const AHREFS_STUDY = "https://ahrefs.com/blog/llmstxt-study/";
const COMMON_CRAWL = "https://commoncrawl.org/blog/a-content-analysis-of-llms-txt-files-from-the-july-2026-crawl-archive";
const LLMSTXT_ORG = "https://llmstxt.org/";

/* Every number the visuals use, in one place, so the visuals cannot disagree with each other.
   Source: our study's generated results (checked October 1, 2026). */
const S = {
  valid: { p: 51.3, lo: 46.3, hi: 56.4, k: 192, n: 374 },
  est: { p: 59.6, lo: 51.5, hi: 67.2, k: 87, n: 146 },
  yc: { p: 46.1, lo: 39.7, hi: 52.5, k: 105, n: 228 },
  rootOrDocs: { p: 64.4, lo: 59.4, hi: 69.2, k: 232, n: 360 },
  withLinks: { p: 88.0, k: 169 },
  specShaped: { p: 75.0, k: 144 },
  noLinks: { p: 12.0, k: 23 },
  ruleYes: { p: 73.1, lo: 62.3, hi: 81.7, k: 57, n: 78 },
  ruleNo: { p: 47.7, lo: 41.9, hi: 53.5, k: 133, n: 279 },
  cfYes: { p: 51.7, lo: 44.4, hi: 58.9, k: 93, n: 180 },
  cfNo: { p: 51.0, lo: 44.0, hi: 58.0, k: 99, n: 194 },
  crawlShape: 49.9,
  crawlNoLinks: 22.6,
};

const f1 = (n) => n.toFixed(1);

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ id, children }) {
  return <h3 id={id} style={{ fontFamily: "var(--display)", fontWeight: 600, fontSize: "clamp(18px, 2.5vw, 22px)", color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.01em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: 14, color: TEXT, background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}
function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function PullQuote({ children }) {
  return (
    <blockquote style={{ margin: "32px 0", padding: "20px 24px", borderLeft: `3px solid ${TEAL}`, fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.4, fontStyle: "normal" }}>
      {children}
    </blockquote>
  );
}
function InlineCTA({ children, href, label }) {
  return (
    <div style={{ margin: "40px 0", padding: "24px 28px", borderRadius: 14, background: TEAL_DIM, border: "1px solid rgba(78,205,196,0.25)" }}>
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>{children}</p>
      <a href={href} style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}>
        {label} &#8594;
      </a>
    </div>
  );
}

const thStyle = { textAlign: "left", fontSize: 11, fontWeight: 700, color: TEXT_DIM, textTransform: "uppercase", letterSpacing: "0.06em", padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.12)", whiteSpace: "nowrap" };
const tdStyle = { fontSize: 14, color: TEXT_DIM, padding: "12px", borderBottom: `1px solid ${CARD_BORDER}`, lineHeight: 1.55, verticalAlign: "top" };
const compactTd = { ...tdStyle, fontSize: 13, padding: "10px 9px", lineHeight: 1.5 };

function TableCard({ title, note, minWidth, children }) {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>{title}</div>
      <div style={{ fontSize: 11, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)", lineHeight: 1.5 }}>{note}</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
        <table style={{ width: "100%", minWidth, borderCollapse: "collapse", fontFamily: "var(--body)" }}>{children}</table>
      </div>
    </div>
  );
}

/* One horizontal bar on a 0 to 100 percent track. lo/hi draw a 95% interval whisker. */
function Bar({ label, value, detail, p, lo, hi, color = TEAL }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", fontFamily: "var(--body)", marginBottom: 7 }}>
        <span style={{ fontSize: 14, color: TEXT, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 13, color: TEXT_DIM }}>
          <span style={{ fontFamily: "var(--mono)", color: TEXT, fontWeight: 700 }}>{value}</span>
          {detail ? <span>{"  "}{detail}</span> : null}
        </span>
      </div>
      <div style={{ position: "relative", height: 14, background: "rgba(255,255,255,0.07)", borderRadius: 7 }}>
        <div style={{ width: `${p}%`, height: "100%", background: color, borderRadius: 7 }} />
        {lo != null ? (
          <>
            <div style={{ position: "absolute", top: 6, left: `${lo}%`, width: `${hi - lo}%`, height: 2, background: TEXT }} />
            <div style={{ position: "absolute", top: 2, left: `${lo}%`, width: 2, height: 10, background: TEXT }} />
            <div style={{ position: "absolute", top: 2, left: `calc(${hi}% - 2px)`, width: 2, height: 10, background: TEXT }} />
          </>
        ) : null}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, label }) {
  return (
    <div role="img" aria-label={label} style={{ margin: "28px 0 36px", border: `1px solid ${TABLE_BORDER}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", background: "rgba(255,255,255,0.03)", borderBottom: `1px solid ${TABLE_BORDER}` }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 13, fontWeight: 700, color: TEAL, letterSpacing: "0.08em", textTransform: "uppercase" }}>{title}</div>
        <div style={{ fontFamily: "var(--body)", fontSize: 13, color: TEXT_DIM, marginTop: 4, lineHeight: 1.5 }}>{subtitle}</div>
      </div>
      <div style={{ padding: "20px" }}>{children}</div>
    </div>
  );
}

function Axis() {
  return (
    <div aria-hidden="true" style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)", fontSize: 11, color: TEXT_DIM, marginTop: -6 }}>
      <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
    </div>
  );
}

/* ── Visual 1: HeadlineStats ── the answer in four numbers, then the same answer as bars with intervals */
function HeadlineStats() {
  const stats = [
    { big: `${f1(S.valid.p)}%`, label: "serve a valid llms.txt at the root", sub: `${S.valid.k} of ${S.valid.n} sites. 95% interval ${f1(S.valid.lo)}% to ${f1(S.valid.hi)}%` },
    { big: `${f1(S.est.p)}%`, label: "of established brands", sub: `Against ${f1(S.yc.p)}% of Y Combinator companies (${S.est.k} of ${S.est.n} and ${S.yc.k} of ${S.yc.n})` },
    { big: `${f1(S.rootOrDocs.p)}%`, label: "when docs subdomains count too", sub: `${S.rootOrDocs.k} of ${S.rootOrDocs.n} sites. 40 publish only on a docs subdomain` },
    { big: "16", label: "of 390 sites left out", sub: "4 domains no longer resolve and 12 never gave our checker a clear answer. The rate stays between 49.7% and 52.8% either way" },
  ];
  return (
    <>
      <div style={{ margin: "28px 0 20px", border: `1px solid ${TABLE_BORDER}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", background: "rgba(255,255,255,0.03)", borderBottom: `1px solid ${TABLE_BORDER}` }}>
          <div style={{ fontFamily: "var(--display)", fontSize: 13, fontWeight: 700, color: TEAL, letterSpacing: "0.08em", textTransform: "uppercase" }}>llms.txt among B2B SaaS sites, October 2026</div>
          <div style={{ fontFamily: "var(--body)", fontSize: 13, color: TEXT_DIM, marginTop: 4 }}>390 sites checked on October 1, 2026. Rates are shares of the sites we could check.</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 1, background: TABLE_BORDER }}>
          {stats.map((s, i) => (
            <div key={s.label} style={{ padding: "22px 20px", background: "#09090b", minWidth: 0 }}>
              <div style={{ fontFamily: "var(--display)", fontSize: 34, fontWeight: 700, color: i === 0 ? TEAL : TEXT, lineHeight: 1.1 }}>{s.big}</div>
              <div style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT, fontWeight: 600, marginTop: 8 }}>{s.label}</div>
              <div style={{ fontFamily: "var(--body)", fontSize: 12.5, color: TEXT_DIM, marginTop: 4, lineHeight: 1.45 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
      <ChartCard
        title="Valid llms.txt, with 95% intervals"
        subtitle="The line on each bar is the 95% interval: the range the true rate for this panel plausibly sits in."
        label={`Share of sites with a valid llms.txt: established brands ${f1(S.est.p)} percent, Y Combinator companies ${f1(S.yc.p)} percent, both cohorts ${f1(S.valid.p)} percent at the root, and ${f1(S.rootOrDocs.p)} percent counting docs subdomains.`}
      >
        <Bar label="Established brands" value={`${f1(S.est.p)}%`} detail={`${S.est.k} of ${S.est.n}  (${f1(S.est.lo)}% to ${f1(S.est.hi)}%)`} p={S.est.p} lo={S.est.lo} hi={S.est.hi} />
        <Bar label="Y Combinator companies" value={`${f1(S.yc.p)}%`} detail={`${S.yc.k} of ${S.yc.n}  (${f1(S.yc.lo)}% to ${f1(S.yc.hi)}%)`} p={S.yc.p} lo={S.yc.lo} hi={S.yc.hi} />
        <Bar label="Both cohorts, site root only" value={`${f1(S.valid.p)}%`} detail={`${S.valid.k} of ${S.valid.n}  (${f1(S.valid.lo)}% to ${f1(S.valid.hi)}%)`} p={S.valid.p} lo={S.valid.lo} hi={S.valid.hi} />
        <Bar label="Both cohorts, root or docs subdomain" value={`${f1(S.rootOrDocs.p)}%`} detail={`${S.rootOrDocs.k} of ${S.rootOrDocs.n}  (${f1(S.rootOrDocs.lo)}% to ${f1(S.rootOrDocs.hi)}%)`} p={S.rootOrDocs.p} lo={S.rootOrDocs.lo} hi={S.rootOrDocs.hi} color={AMBER} />
        <Axis />
      </ChartCard>
    </>
  );
}

/* ── Visual 2: StudyComparison ── other studies, different populations */
function StudyComparison() {
  const rows = [
    { name: "Independent top-million crawl", href: "https://frank.sauerburger.io/2026/09/08/llms-txt-adoption-one-year-later.html", pop: "Top 1,000,000 domains", when: "Aug 2026", share: "3.53%", how: "Plain-text response required" },
    { name: "HTTP Archive analysis", href: "https://caseyrb.com/blog/state-of-llms-txt-adoption/", pop: "Top 10,000 sites (7,504 crawled)", when: "Jun 2026", share: "5.61%", how: "Valid, parseable file; soft-404 pages screened out" },
    { name: "GEO/AEO Playbooks", href: "https://geoplaybooks.com/data/llms-txt-adoption-2026/", pop: "Top 5,000 domains", when: "Jul 2026", share: "7.5%", how: "Spec-valid file; 1,590 non-responding hosts reported separately" },
    { name: "Rankability", href: "https://www.rankability.com/blog/llms-txt-adoption/", pop: "Top 10,000 domains", when: "Sep 2026", share: "8.3%", how: "llms.txt or llms-full.txt; sites it could not check stay in the base" },
    { name: "SE Ranking", href: "https://seranking.com/blog/llms-txt/", pop: "Nearly 300,000 domains", when: "Published Nov 2025", share: "10.13%", how: "Not stated" },
    { name: "Ahrefs", href: AHREFS_STUDY, pop: "137,210 Ahrefs Web Analytics sites", when: "May 2026", share: "28%", how: "Root returns 200 with Markdown; Ahrefs calls it an upper bound" },
    { name: "Marketing Graham audit, reported by IT Brief", href: "https://itbrief.co.uk/story/most-uk-b2b-tech-sites-miss-ai-readability-signals", pop: "752 UK tech companies", when: "Reported Oct 2026", share: "About a quarter", how: "A genuine, non-empty file; method not published" },
    { name: "llmtxt.info tracker", href: "https://llmtxt.info/blog/state-of-llms-txt-2026/", pop: "219 hand-picked well-known hosts (216 tested)", when: "Sep 2026", share: "57.4% (SaaS sector: 68.1%, 47 hosts)", how: "200, plain text, valid title; unreachable hosts left out" },
    { name: "This study", href: null, pop: "390 B2B SaaS sites (374 checkable)", when: "Oct 2026", share: `${f1(S.valid.p)}% (${f1(S.valid.lo)}% to ${f1(S.valid.hi)}%); established ${f1(S.est.p)}%, Y Combinator ${f1(S.yc.p)}%`, how: "Text file that opens with a title; unreachable and bot-walled sites left out", ours: true },
  ];
  return (
    <TableCard
      title="Published llms.txt adoption figures, side by side"
      note="Each row counts a different population on a different date with a different test. This is context, not a ranking."
      minWidth={620}
    >
      <thead>
        <tr>
          {["Study", "Who was counted", "When", "Share with an llms.txt", "How a file counted"].map((h) => (
            <th key={h} style={thStyle}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} style={r.ours ? { background: TEAL_DIM } : undefined}>
            <td style={{ ...compactTd, color: r.ours ? TEAL : TEXT, fontWeight: 700 }}>
              {r.href ? <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: TEXT, textDecoration: "underline", fontWeight: 700 }}>{r.name}</a> : r.name}
            </td>
            <td style={compactTd}>{r.pop}</td>
            <td style={compactTd}>{r.when}</td>
            <td style={{ ...compactTd, color: r.ours ? TEAL : TEXT, fontWeight: 700 }}>{r.share}</td>
            <td style={compactTd}>{r.how}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

/* ── Visual 3: FileShape ── what the valid files contain, next to the web-wide corpus */
function FileShape() {
  return (
    <ChartCard
      title="What the 192 valid files contain"
      subtitle="Valid means the file opens with a title. The full spec shape adds a summary, a section and a link."
      label={`Of 192 valid files, ${S.withLinks.k} have at least one link, ${S.specShaped.k} have the full spec shape, and ${S.noLinks.k} have no links. Web-wide, Common Crawl found ${f1(S.crawlShape)} percent with the full shape and ${f1(S.crawlNoLinks)} percent with no links.`}
    >
      <Bar label="Valid: opens with a title" value="192" detail="100%" p={100} />
      <Bar label="At least one link" value={`${S.withLinks.k}`} detail={`${f1(S.withLinks.p)}%`} p={S.withLinks.p} />
      <Bar label="Full spec shape: title, summary, a section and a link" value={`${S.specShaped.k}`} detail={`${f1(S.specShaped.p)}%`} p={S.specShaped.p} />
      <Bar label="No links at all" value={`${S.noLinks.k}`} detail={`${f1(S.noLinks.p)}%`} p={S.noLinks.p} color={AMBER} />
      <Axis />
      <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${TABLE_BORDER}` }}>
        <div style={{ fontFamily: "var(--body)", fontSize: 12, fontWeight: 700, color: TEXT, marginBottom: 10 }}>Our panel next to the web at large</div>
        <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", minWidth: 420, borderCollapse: "collapse", fontFamily: "var(--body)" }}>
            <thead>
              <tr>
                {["Share of files", "Our 192 valid files", "Common Crawl, 584,107 files"].map((h, i) => (
                  <th key={h} style={{ ...thStyle, textAlign: i === 0 ? "left" : "right" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ ...tdStyle, color: TEXT, fontWeight: 600 }}>Full spec shape</td>
                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--mono)", color: TEAL, fontWeight: 700 }}>{f1(S.specShaped.p)}%</td>
                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--mono)" }}>{f1(S.crawlShape)}%</td>
              </tr>
              <tr>
                <td style={{ ...tdStyle, color: TEXT, fontWeight: 600 }}>No links at all</td>
                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--mono)", color: TEAL, fontWeight: 700 }}>{f1(S.noLinks.p)}%</td>
                <td style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--mono)" }}>{f1(S.crawlNoLinks)}%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontFamily: "var(--body)", fontSize: 11.5, color: TEXT_DIM, marginTop: 10, lineHeight: 1.5 }}>
          Different populations and similar, not identical, definitions of the full shape. Common Crawl&apos;s July 2026 crawl covers files from across the web.
        </div>
      </div>
    </ChartCard>
  );
}

/* ── Visual 4: RobotsLink ── the robots.txt cross-tab, plus the Cloudflare non-result */
function RobotsLink() {
  return (
    <ChartCard
      title="Valid llms.txt by robots.txt stance, with 95% intervals"
      subtitle="An association between two scans 16 days apart, not a cause."
      label={`${f1(S.ruleYes.p)} percent of sites that name AI crawlers in robots.txt publish a valid llms.txt, against ${f1(S.ruleNo.p)} percent of sites that do not. Cloudflare sites publish at ${f1(S.cfYes.p)} percent against ${f1(S.cfNo.p)} percent for the rest.`}
    >
      <Bar label="Name AI crawlers in robots.txt" value={`${f1(S.ruleYes.p)}%`} detail={`${S.ruleYes.k} of ${S.ruleYes.n}  (${f1(S.ruleYes.lo)}% to ${f1(S.ruleYes.hi)}%)`} p={S.ruleYes.p} lo={S.ruleYes.lo} hi={S.ruleYes.hi} />
      <Bar label="Do not name AI crawlers" value={`${f1(S.ruleNo.p)}%`} detail={`${S.ruleNo.k} of ${S.ruleNo.n}  (${f1(S.ruleNo.lo)}% to ${f1(S.ruleNo.hi)}%)`} p={S.ruleNo.p} lo={S.ruleNo.lo} hi={S.ruleNo.hi} color="#7d8590" />
      <Bar label="Served through Cloudflare" value={`${f1(S.cfYes.p)}%`} detail={`${S.cfYes.k} of ${S.cfYes.n}  (${f1(S.cfYes.lo)}% to ${f1(S.cfYes.hi)}%)`} p={S.cfYes.p} lo={S.cfYes.lo} hi={S.cfYes.hi} />
      <Bar label="Not served through Cloudflare" value={`${f1(S.cfNo.p)}%`} detail={`${S.cfNo.k} of ${S.cfNo.n}  (${f1(S.cfNo.lo)}% to ${f1(S.cfNo.hi)}%)`} p={S.cfNo.p} lo={S.cfNo.lo} hi={S.cfNo.hi} color="#7d8590" />
      <Axis />
      <div style={{ fontFamily: "var(--body)", fontSize: 11.5, color: TEXT_DIM, marginTop: 14, lineHeight: 1.5 }}>
        The robots.txt comparison uses the 357 sites with a readable robots.txt on September 15, 2026 and a clear llms.txt answer. The Cloudflare comparison uses all 374 checkable sites. Within cohorts, Y Combinator companies: 73.9% against 41.6%. Established brands: 71.9% against 56.6%.
      </div>
    </ChartCard>
  );
}

/* ── Visual 5: ClaimCheck ── the adopter list, checked where each file actually is */
function ClaimCheck() {
  const rows = [
    ["Vercel", "vercel.com/llms.txt", "Yes", true],
    ["Zapier", "zapier.com/llms.txt", "Yes", true],
    ["GitBook", "gitbook.com/llms.txt", "Yes", true],
    ["Mintlify", "mintlify.com/llms.txt", "Yes", true],
    ["Yoast", "yoast.com/llms.txt", "Yes", true],
    ["Mastercard Developers", "developer.mastercard.com/llms.txt", "Yes, on its developer portal host", true],
    ["Anthropic", "docs.anthropic.com/llms.txt, which redirects to platform.claude.com/llms.txt", "No. anthropic.com/llms.txt is a 404", false],
    ["Hugging Face", "huggingface.co/docs/hub/llms.txt and huggingface.co/docs/transformers/llms.txt", "No. huggingface.co/llms.txt is a 404", false],
  ];
  return (
    <TableCard
      title="Where well-known adopters keep their llms.txt"
      note="Checked October 1, 2026 with a plain request. A check of named companies, not a sample."
      minWidth={540}
    >
      <thead>
        <tr>
          {["Company", "Where the file is", "A file at the site root?"].map((h) => (
            <th key={h} style={{ ...thStyle, padding: "10px 9px" }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(([company, where, root, yes]) => (
          <tr key={company}>
            <td style={{ ...compactTd, color: TEXT, fontWeight: 600 }}>{company}</td>
            <td style={{ ...compactTd, fontFamily: "var(--mono)", fontSize: 12, overflowWrap: "anywhere" }}>{where}</td>
            <td style={{ ...compactTd, color: yes ? TEAL : AMBER, fontWeight: 700, overflowWrap: "anywhere" }}>{root}</td>
          </tr>
        ))}
      </tbody>
    </TableCard>
  );
}

const faqs = [
  {
    q: "How many B2B SaaS companies have an llms.txt?",
    a: "About half of the ones we checked. On October 1, 2026, 192 of 374 checkable B2B SaaS sites (51.3%, 95% interval 46.3% to 56.4%) served a valid llms.txt at their root: 59.6% of established brands and 46.1% of Y Combinator companies. It is a fixed panel of 390 sites, so it describes those sites, not B2B SaaS as a whole.",
  },
  {
    q: "Which companies use llms.txt?",
    a: "In our October 1, 2026 check, Vercel, Zapier, GitBook, Mintlify, Yoast and Mastercard's developer portal serve a valid llms.txt at the root of the host we checked. Anthropic serves one on its developer docs rather than anthropic.com, and Hugging Face on its documentation subpaths. These companies publish a file; we did not test whether anything reads it.",
  },
  {
    q: "Where should an llms.txt go, the root or a docs subdomain?",
    a: "The specification allows a file at the root or at any subpath, and only the title is required. Put one at the root, and publish on your docs subdomain too if your documentation lives there. In our panel, 40 sites published only on a docs subdomain, which lifts the rate from 51.3% to 64.4% once those files are counted.",
  },
  {
    q: "What makes an llms.txt valid?",
    a: "Under the llmstxt.org specification the only required element is a title: a markdown H1 as the first line. A short summary in a quote block, sections of links and an optional section are recommended. In our panel 192 files met the title test, and 144 of them also had a summary, a section and a link.",
  },
  {
    q: "Does llms.txt actually work?",
    a: "Our study cannot say, because it measures what is published, not what is read. Ahrefs found that 97% of about 38,000 valid files on its customers' sites received no requests at all in May 2026. Whether any AI system relies on the file for its answers is a separate question that this data does not test.",
  },
  {
    q: "Does Google use llms.txt?",
    a: "Not according to Google. Its guide to generative AI features in Search says Google Search does not use llms.txt, and that publishing one neither helps nor harms your visibility there. It adds that a file is fine to publish for other services that use these files. The guide covers Google Search only, not other AI systems.",
  },
  {
    q: "Can a site block AI crawlers and still publish an llms.txt?",
    a: "Yes. They are separate files: robots.txt sets crawl rules and llms.txt is a content map. Of the 16 sites in our panel that block at least one of the 17 AI crawlers we track, 11 also publish a valid llms.txt. Sixteen sites is too few for firm conclusions, so treat that as description only.",
  },
];

const datasetSchema = {
  "@context": "https://schema.org",
  "@type": "Dataset",
  name: "AI-Advisors llms.txt Adoption Study: B2B SaaS panel, October 2026",
  description:
    "Whether each of 390 B2B SaaS websites (153 established brands and 237 active Y Combinator B2B SaaS companies) serves a valid /llms.txt, checked on October 1, 2026, with the shape of each file and the site's AI-crawler robots.txt stance from the AI Bot Blocking Index.",
  url: POST_URL,
  creator: { "@type": "Organization", name: "AI-Advisors", url: "https://www.ai-advisors.ai", "@id": "https://www.ai-advisors.ai/#organization" },
  publisher: { "@id": "https://www.ai-advisors.ai/#organization" },
  dateCreated: "2026-10-01",
  datePublished: POST_DATE,
  license: "https://creativecommons.org/licenses/by/4.0/",
  isAccessibleForFree: true,
  temporalCoverage: "2026-10-01",
  spatialCoverage: "B2B SaaS websites (global)",
  measurementTechnique:
    "One HTTP GET of /llms.txt per domain with redirects followed, a content test that the response is a text file whose first meaningful line is a markdown H1, one re-check of unanswered sites, and a second-tool check of every classified site",
  distribution: [
    { "@type": "DataDownload", encodingFormat: "text/csv", contentUrl: `https://www.ai-advisors.ai${CSV_URL}` },
    { "@type": "DataDownload", encodingFormat: "application/json", contentUrl: `https://www.ai-advisors.ai${JSON_URL}` },
  ],
};

function Content() {
  return (
    <article>
      {/* Overview box */}
      <div style={{ background: TEAL_DIM, border: `1px solid ${TEAL_BORDER}`, borderRadius: 12, padding: "24px 28px", margin: "0 0 36px" }}>
        <P>
          <Strong>About half.</Strong> Of the 374 B2B SaaS sites we could check on October 1, 2026, 192 serve a valid <A href="/glossary/llms-txt">llms.txt</A> at their root (51%), and 64% do once docs subdomains count. The files vary a lot, and publishing one says nothing about whether an AI system reads it.
        </P>
        <ul style={{ paddingLeft: 20, margin: "0 0 4px" }}>
          <Li><Strong>Established ahead.</Strong> 59.6% of established brands publish one, against 46.1% of Y Combinator companies</Li>
          <Li><Strong>A low bar.</Strong> One in four valid files lacks the spec&apos;s full shape, and 23 have no links</Li>
          <Li><Strong>Docs hide a lot.</Strong> 40 sites publish only on a docs subdomain</Li>
          <Li><Strong>Linked to robots.txt.</Strong> 73% of sites that name AI crawlers there publish one, against 48% of the rest (an association)</Li>
        </ul>
      </div>

      <H2 id="how-many">How many B2B SaaS sites publish a valid llms.txt?</H2>
      <P>
        <Strong>About half.</Strong> Of the 374 sites in our panel we could check, 192 (51.3%) serve a valid llms.txt at the root. Established brands lead Y Combinator companies, 59.6% to 46.1%, and counting docs subdomains lifts the total to 64.4%.
      </P>
      <HeadlineStats />
      <P>
        &quot;Valid&quot; has one test, the one the <A href={LLMSTXT_ORG}>llms.txt specification</A> treats as required: the file opens with a title, a markdown H1. We counted a site only when the response was a text file that passed it, not an HTML page that happened to return a 200. Four panel domains no longer resolve and 12 sites never gave our checker a clear answer, mostly because of bot walls, so they sit outside the rates. Whichever way those 12 would fall, the rate stays between 49.7% and 52.8%.
      </P>
      <P>
        The established cohort leads by 13.5 points (95% interval 3.2 to 23.5), a gap too large to put down to chance alone. That cohort is hand-picked from recognizable brands. The Y Combinator cohort is a filter on a public directory of growth-stage companies. Treat both as panels, not as a census of B2B SaaS.
      </P>

      <H3 id="docs-subdomains">Counting docs subdomains too</H3>
      <P>
        Forty sites publish an llms.txt only on a docs subdomain such as <Code>docs.company.com</Code>, and nowhere at the root. DigitalOcean, Docker, Snowflake and LaunchDarkly are four of them. Counting those files, 232 of 360 sites (64.4%) publish somewhere we looked. A study that probes only the site root misses all 40.
      </P>
      <P>
        The specification allows a file at the root or at any subpath, so one more convention was worth a check: <Code>/docs/llms.txt</Code> on the main domain. None of the 147 sites without a root or docs-subdomain file had a valid file there, so it adds nothing in this panel. We added that check after seeing the first results, and other subpaths were not probed.
      </P>
      <PullQuote>
        Counting only the site root misses about 13 points: 40 B2B SaaS sites publish an llms.txt on a docs subdomain and nowhere else.
      </PullQuote>

      <H2 id="why-rates-disagree">Why published llms.txt adoption rates disagree</H2>
      <P>
        <Strong>Different populations.</Strong> Each study counts a different one. Ranked lists of the biggest domains read 3.5% to 8.3%, Ahrefs&apos; own customers 28%, UK B2B tech companies about a quarter, and a hand-picked panel of well-known hosts 57%. None tests the same thing.
      </P>
      <StudyComparison />
      <P>
        Nobody in the table is wrong. Ahrefs calls its 28% an upper bound because its customers skew technical, and ranked lists of top domains include infrastructure hosts that never serve a web page. The row closest to ours in method is the llmtxt.info tracker, which uses the same test (a 200, plain text, a valid title) and leaves unreachable sites out of the base. It reads 57.4% on a hand-picked panel of well-known hosts.
      </P>
      <P>
        Our {f1(S.valid.p)}% describes a panel of sites in this category, picked from a brand list and a startup directory rather than found by crawling the web. It does not describe the web. <A href="https://seranking.com/blog/llms-txt/">SE Ranking&apos;s study</A>, published in November 2025, is the oldest row, so read it as a baseline rather than today&apos;s figure.
      </P>

      <H2 id="what-files-contain">Valid is the minimum: what the files contain</H2>
      <P>
        <Strong>A title is enough.</Strong> A valid file only has to open with one. Of our 192, 144 (75%) have the spec&apos;s full shape of title, summary and linked sections, and 23 have no links at all. The median file is 9.7 KB.
      </P>
      <FileShape />
      <P>
        The specification asks for a title, a short summary in a quote block, and sections of links. Only the title is required, so a one-line file passes. Of the 192 valid files, 155 (80.7%) have the summary, 184 (95.8%) have at least one section, 169 (88.0%) have at least one link, and 55 (28.6%) use the optional section for pages a reader can skip. 144 have all of it.
      </P>
      <P>
        The 23 files with no links (12.0%) include several short notes addressed to AI crawlers rather than maps of the site, and one valid file is a for-sale notice from a parked domain. At the other end, 17 files (8.9%) run past 100 KB and the largest is 1.52 MB. The median file has about 76 lines and 48 links.
      </P>
      <P>
        Ten more sites publish something that looks like an llms.txt but fails the title test, such as a list of links with no title, or a title preceded by a plugin credit line. We left them out of the 51%. Counting them would make it 54%.
      </P>
      <P>
        Next to <A href={COMMON_CRAWL}>Common Crawl&apos;s July 2026 analysis</A> of 584,107 files from across the web, our B2B SaaS files come out more complete on both measures in the comparison above. The populations differ, and so do the definitions of the complete shape, so read the contrast as a direction, not a precise gap.
      </P>
      <P>
        Publishing is not always a choice. Common Crawl found that two thirds of the files it analyzed are templated, mostly by plugins and site builders. We did not measure generators, and only five of our 192 valid files carry a generator signature, so we draw no conclusion for the panel.
      </P>
      <PullQuote>
        A valid llms.txt only has to open with a title. One in four of the valid files in our panel stops short of the spec&apos;s full structure.
      </PullQuote>

      <H2 id="robots-link">Do sites that name AI crawlers in robots.txt publish more often?</H2>
      <P>
        <Strong>Yes.</Strong> 57 of 78 sites with an explicit AI-crawler rule (73.1%) also publish a valid llms.txt, against 133 of 279 without one (47.7%). It is an association, not a cause, and Cloudflare use shows no difference.
      </P>
      <RobotsLink />
      <P>
        We scanned the robots.txt of the same 390 sites 16 days earlier for our <A href="/research/ai-bot-blocking-index/2026-q3">AI Bot Blocking Index</A>. A site counts as naming <A href="/glossary/ai-crawlers">AI crawlers</A> when its robots.txt mentions at least one of the 17 we track, whether it lets that crawler in or keeps it out. The comparison uses the 357 sites with a readable robots.txt and a clear llms.txt answer.
      </P>
      <P>
        The link is strongest among Y Combinator companies: 73.9% (34 of 46) of those that name AI crawlers publish a valid file, against 41.6% (69 of 166) of those that do not. Among established brands it points the same way, 71.9% against 56.6%, but that gap is within what chance could produce (95% interval from -4.1 to +30.8 points).
      </P>
      <P>
        Blocking is not the opposite of publishing. Of the 16 sites that block at least one tracked crawler, 11 publish a valid llms.txt. Sixteen sites is too few for firm conclusions, so treat that as description only. For which crawlers are worth letting in, see <A href="/blog/robots-txt-ai-bots-2026">which AI crawlers to allow</A>. Cloudflare use shows no link at all: 51.7% of sites served through Cloudflare publish a valid file, against 51.0% of the rest.
      </P>
      <P>
        For the robots.txt side of the story, see <A href="/blog/are-b2b-saas-companies-blocking-ai-crawlers">what that scan found about blocking</A>.
      </P>

      <H2 id="adopter-list">Where the well-known adopters keep their llms.txt</H2>
      <P>
        <Strong>Mostly at the root.</Strong> Vercel, Zapier, GitBook, Mintlify, Yoast and Mastercard&apos;s developer portal serve one there. Anthropic&apos;s is on its developer docs, and Hugging Face serves them on documentation subpaths rather than at its root.
      </P>
      <ClaimCheck />
      <P>
        Anthropic&apos;s file moved with its documentation: <Code>docs.anthropic.com/llms.txt</Code> now redirects to <Code>platform.claude.com/llms.txt</Code>, while <Code>anthropic.com/llms.txt</Code> returns a 404. Hugging Face&apos;s root returns a 404 too, but its Hub and Transformers documentation each serve a file.
      </P>
      <P>
        Lists of llms.txt adopters tend to name companies without saying where each file lives. That matters because a check of the site root alone would miss Anthropic&apos;s and Hugging Face&apos;s files.
      </P>

      <H2 id="what-to-do">What to do if your B2B SaaS site has no valid llms.txt</H2>
      <P>
        <Strong>Start with a title.</Strong> Publish one file at your root with your company name as the title, a one-line summary and links to your key pages, served as plain text. If your docs live on a subdomain, publish there too. Then fetch it yourself to confirm.
      </P>
      <ol style={{ paddingLeft: 22, margin: "0 0 18px" }}>
        <Li><Strong>Pick the host.</Strong> Publish at the root. If your documentation lives on a subdomain, publish there too: that is where 40 of our publishers keep the only file they have.</Li>
        <Li><Strong>Open with a title.</Strong> The first line is <Code># Your Company</Code>, followed by a one-line summary in a quote block, then sections of links with a few words about each page.</Li>
        <Li><Strong>Serve plain text.</Strong> Return a 200 with a text content type, not an HTML page, and do not redirect the path to your homepage. Seventeen panel sites answered with an HTML page instead of a file.</Li>
        <Li><Strong>Fetch it yourself.</Strong> Request the URL the way a crawler would and confirm the first line is the title. A catch-all page that returns 200 for every path looks published and is not.</Li>
        <Li><Strong>Keep it current.</Strong> List the pages a buyer or an AI assistant would need first, and update the file when those pages move.</Li>
      </ol>
      <P>A minimal file in that layout (<A href="/blog/what-is-llms-txt">our llms.txt explainer</A> has longer starters for SaaS, docs and blog sites):</P>
      <CodeBlock lang="llms.txt minimal example" syntax="markdown">{`# Blaze CRM

> Blaze CRM is a CRM for B2B sales teams, with pipeline reporting built in.

## Product

- [Pipeline reporting](https://blazecrm.com/reporting): Forecasts by stage.
- [Pricing](https://blazecrm.com/pricing): Plans and what each includes.

## Docs

- [API reference](https://docs.blazecrm.com/api): Every endpoint.

## Optional

- [Changelog](https://blazecrm.com/changelog): Release notes by month.`}</CodeBlock>
      <InlineCTA href="/tools/llms-txt-generator" label="Build your file with the free llms.txt Generator">
        The free llms.txt Generator builds a file in this layout from a short form: a title, a summary and sections of links. No signup.
      </InlineCTA>
      <P>
        llms.txt is one small piece of <A href="/glossary/answer-engine-optimization">answer engine optimization</A>. The <A href="/tools/aeo-audit">free AEO audit</A> includes an llms.txt check among its 29, and the <A href="/platform/aeo">AEO platform</A> scores your site for AI citation with page-level recommendations.
      </P>

      <H2 id="limits">What this study cannot tell you</H2>
      <P>
        <Strong>Published, not read.</Strong> This study measures what sites publish, not what AI systems fetch or use, and the panel is hand-picked, not random. Treat the rates as a description of these 390 sites.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Use.</Strong> <A href={GOOGLE_GUIDE}>Google&apos;s own guide</A> says Google Search does not use llms.txt, and that publishing one neither helps nor harms visibility there. In its <A href={AHREFS_STUDY}>May 2026 study</A>, Ahrefs found that 97% of about 38,000 valid files on its customers&apos; sites received no requests at all. Nothing here shows that publishing a file changes your <A href="/glossary/ai-citation">AI citations</A>.</Li>
        <Li><Strong>The panel.</Strong> 153 hand-picked established brands and 237 Y Combinator companies, so the rates belong to these sites. Some panel domains no longer run their own site, and ten valid files are served from a different domain than the one on our list: eight from a site that forwards to another domain, one from a GitHub gist and one from a website builder&apos;s file host. Counting those ten as not published gives 48.7%.</Li>
        <Li><Strong>Where we looked.</Strong> The site root, plus <Code>docs.</Code> and <Code>/docs/</Code> for sites without a root file. A file on any other subpath is missed.</Li>
        <Li><Strong>Silent sites.</Strong> Twelve sites never gave our checker a clear answer, including on a re-check 32 minutes later. We left them out instead of counting them as misses. Two of the twelve did answer a browser&apos;s user agent, one with a valid file and one with none, and counting those two leaves the rate at 51.3%.</Li>
        <Li><Strong>Intent.</Strong> Plugins and documentation platforms write files by default, so a published file does not prove a team chose to publish it.</Li>
        <Li><Strong>One snapshot.</Strong> Everything was checked on October 1, 2026, and files change.</Li>
      </ul>
      <P>
        To see whether anything fetches your own file, <A href="/blog/how-to-track-ai-bot-activity">read your own server logs</A>, or <A href="/platform/ai-analytics">track AI bot activity</A> over time.
      </P>

      <H2 id="method">How we measured</H2>
      <P>
        <Strong>One request per site.</Strong> On October 1, 2026 we fetched /llms.txt from 390 B2B SaaS sites, 153 established brands and 237 Y Combinator companies, and counted a site as publishing only when the response was a text file that opens with a title.
      </P>
      <P>
        The panel is the one behind our <A href="/research/ai-bot-blocking-index">AI Bot Blocking Index</A>: hand-picked established B2B SaaS brands, plus active B2B SaaS companies from the Y Combinator directory (status active, public or acquired; team size 10 or more). Each request was a plain GET of <Code>https://domain/llms.txt</Code> under our own checker&apos;s user agent, with redirects followed and the body read up to 2 MB.
      </P>
      <P>
        Every site ends in one of five classes. <Strong>Valid</Strong> is a text file whose first meaningful line is a title. <Strong>Invalid</Strong> is a non-HTML file that fails that test. <Strong>Absent</Strong> includes 404s and HTML pages returned for the path. <Strong>Inconclusive</Strong> covers bot walls and errors that persisted after one re-check at least 30 minutes later, and <Strong>unreachable</Strong> means the domain does not resolve. The last two are left out of the rates and never counted as misses.
      </P>
      <P>
        We fixed the question, the panel, the test and the analyses before the first scan, then checked our own scanner by re-fetching every site it had classed as valid, absent or invalid with a second tool (curl). That check found one mistake, a bot challenge counted as an empty file, so we fixed it and re-ran the whole scan. A few checks were added after we saw first results and are labelled that way: the <Code>/docs/llms.txt</Code> check, the count that sets aside files served from another domain, and the split of the robots.txt link by cohort. Intervals are 95% confidence intervals, and group comparisons use Fisher&apos;s exact test.
      </P>
      <P>
        Every site and class is downloadable under CC BY 4.0 as a <A href={CSV_URL}>CSV file</A> or a <A href={JSON_URL}>JSON file</A>, with a data dictionary inside the JSON. The robots.txt columns come from the Index scan of September 15, 2026. Cite it as: AI-Advisors llms.txt Adoption Study, B2B SaaS panel, October 1, 2026 (CC BY 4.0). Our own llms.txt passes the same test.
      </P>

      {/* Dataset JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(datasetSchema) }} />

      {/* FAQPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* FAQ section */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* Related Reading */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/what-is-llms-txt">What Is llms.txt and Does Your Website Need One?</A></Li>
        <Li><A href="/blog/are-b2b-saas-companies-blocking-ai-crawlers">Are B2B SaaS Companies Blocking AI Crawlers? September 2026 Data</A></Li>
        <Li><A href="/blog/robots-txt-ai-bots-2026">AI Crawlers in 2026: Which Bots to Allow and Which to Block</A></Li>
        <Li><A href="/blog/how-to-track-ai-bot-activity">How to Track AI Bot Activity on Your Website</A></Li>
        <Li><A href="/blog/schema-markup-for-aeo-tiered-guide">Schema Markup for AEO: The Tiered Guide (With Copy-Paste JSON-LD)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "do-b2b-saas-companies-publish-llms-txt",
  title: "Do B2B SaaS Companies Publish an llms.txt? October 2026 Data",
  seoTitle: "Do B2B SaaS Sites Publish an llms.txt? October 2026 Data",
  excerpt: "390 B2B SaaS sites, one test: does the site serve a valid llms.txt? About half do, but a quarter of those files lack the spec's full structure.",
  metaDescription: "390 B2B SaaS sites checked in October 2026: about half publish a valid llms.txt, 64% counting docs subdomains. What the files contain and how studies compare.",
  date: POST_DATE,
  readTime: "13 min read",
  category: "Answer Engine Optimization",
  tags: ["llms.txt", "llms.txt adoption", "B2B SaaS", "AEO", "AI crawlers", "robots.txt", "adoption statistics"],
  fiveA: "aeo",
  Content,
};

export default post;
